import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../api';
import AppShell from '../components/AppShell';

const UNITS = { piece:'шт', liter:'л', gram:'кг' };
const PAYMENT_LABELS = { cash:'💵 Готівка', card:'💳 Картка', mixed:'🔀 Змішана', bonus:'🎁 Бонуси' };
const PAYMENT_COLORS = { cash:'#5fbf8f', card:'#6cb6ff', mixed:'#f5a623', bonus:'#ff6b6b' };
const CHART_COLOR = '#f5a623';
const CHART_COLOR2 = '#6cb6ff';

function StatCard({ icon, label, value, sub, color='#fff' }) {
  return (
    <div style={cardStyles.wrap}>
      <div style={cardStyles.icon}>{icon}</div>
      <div style={cardStyles.body}>
        <div style={{...cardStyles.value, color}}>{value}</div>
        <div style={cardStyles.label}>{label}</div>
        {sub && <div style={cardStyles.sub}>{sub}</div>}
      </div>
    </div>
  );
}

const cardStyles = {
  wrap: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'16px 20px', display:'flex', alignItems:'center', gap:'14px', flex:1, minWidth:'160px' },
  icon: { fontSize:'28px' },
  body: { flex:1 },
  value: { fontSize:'22px', fontWeight:'bold', marginBottom:'2px' },
  label: { color:'#9a8a6e', fontSize:'12px' },
  sub: { color:'#9a8a6e', fontSize:'11px', marginTop:'2px' },
};

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { id: storeIdFromRoute } = useParams();
  const isStoreContext = !!storeIdFromRoute;

  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(storeIdFromRoute || null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('sales');

  // Дані
  const [summary, setSummary] = useState(null);
  const [byDay, setByDay] = useState([]);
  const [byHour, setByHour] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [byStore, setByStore] = useState([]);
  const [stocks, setStocks] = useState(null);
  const [movements, setMovements] = useState([]);
  const [movDays, setMovDays] = useState(7);
  const [stockFilter, setStockFilter] = useState('all');
  const [stockSearch, setStockSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stores/').then(r => setStores(r.data)).catch(() => {});
    loadAll();
  }, []);

  useEffect(() => {
    loadSalesData();
  }, [selectedStoreId, days]);

  useEffect(() => {
    if (selectedStoreId) {
      loadStockData();
      loadMovements(selectedStoreId, movDays);
    }
  }, [selectedStoreId]);

  async function loadAll() {
    setLoading(true);
    await Promise.all([loadSalesData(), loadStockData()]);
    setLoading(false);
  }

  async function loadSalesData() {
    const storeQ = selectedStoreId ? `&store_id=${selectedStoreId}` : '';
    const [sumRes, dayRes, hourRes, topRes, storeRes] = await Promise.all([
      api.get(`/analytics/sales/summary?days=${days}${storeQ}`).catch(() => ({ data: null })),
      api.get(`/analytics/sales/by-day?days=${days}${storeQ}`).catch(() => ({ data: [] })),
      api.get(`/analytics/sales/by-hour?days=7${storeQ}`).catch(() => ({ data: [] })),
      api.get(`/analytics/sales/top-products?days=${days}${storeQ}`).catch(() => ({ data: [] })),
      api.get(`/analytics/sales/by-store?days=${days}`).catch(() => ({ data: [] })),
    ]);
    setSummary(sumRes.data);
    setByDay(dayRes.data);
    setByHour(hourRes.data);
    setTopProducts(topRes.data);
    setByStore(storeRes.data);
  }

  async function loadStockData() {
    if (!selectedStoreId) {
      const res = await api.get('/analytics/stocks').catch(() => ({ data: [] }));
      setStocks({ network: res.data });
      return;
    }
    const url = stockFilter === 'all'
      ? `/analytics/stocks/${selectedStoreId}`
      : `/analytics/stocks/${selectedStoreId}?filter=${stockFilter}`;
    const res = await api.get(url).catch(() => ({ data: null }));
    setStocks(res.data);
  }

  async function loadMovements(storeId, d) {
    const res = await api.get(`/analytics/movements/summary/${storeId}?days=${d}`).catch(() => ({ data: [] }));
    setMovements(res.data);
  }

  async function changeStockFilter(f) {
    setStockFilter(f);
    if (!selectedStoreId) return;
    const url = f === 'all'
      ? `/analytics/stocks/${selectedStoreId}`
      : `/analytics/stocks/${selectedStoreId}?filter=${f}`;
    const res = await api.get(url).catch(() => ({ data: null }));
    setStocks(res.data);
  }

  const storeName = stores.find(s => s.id === selectedStoreId)?.name || 'Вся мережа';
  const paymentData = summary ? Object.entries(summary.payment_methods).map(([k, v]) => ({
    name: PAYMENT_LABELS[k] || k, value: parseFloat(v), color: PAYMENT_COLORS[k] || '#9a8a6e'
  })) : [];

  const filteredStockProducts = stocks?.products?.filter(p =>
    p.name.toLowerCase().includes(stockSearch.toLowerCase())
  ) || [];

  const tabs = [
    { key:'sales', label:'💰 Продажі' },
    { key:'stocks', label:'📦 Залишки' },
  ];

  return (
    <AppShell
      title="📊 Аналітика"
      onBack={() => navigate(isStoreContext ? `/store/${storeIdFromRoute}` : '/dashboard')}
    >
      {/* Фільтри: магазин + період */}
      <div className="row-between" style={{ marginBottom:16 }}>
        <select style={{...styles.storeSelect, maxWidth:240}}
          value={selectedStoreId || ''}
          onChange={e => setSelectedStoreId(e.target.value || null)}>
          {!isStoreContext && <option value="">🌐 Вся мережа</option>}
          {stores.map(s => <option key={s.id} value={s.id}>🏪 {s.name}</option>)}
        </select>
        <div style={styles.daysBtns}>
          {[7, 30, 90].map(d => (
            <button key={d}
              style={days===d ? styles.dayBtnActive : styles.dayBtn}
              onClick={() => setDays(d)}>
              {d}д
            </button>
          ))}
        </div>
      </div>

      {/* Вкладки */}
      <div className="tabbar">
        {tabs.map(t => (
          <button key={t.key}
            className={`tab ${activeTab===t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div>

        {/* ══ ПРОДАЖІ ══ */}
        {activeTab === 'sales' && (
          <div>
            {/* Карточки */}
            <div style={styles.statsRow}>
              <StatCard icon="💵" label="Виручка" value={`${parseFloat(summary?.total_revenue||0).toFixed(2)} ₴`} color="#f5a623" sub={`за ${days} днів`} />
              <StatCard icon="🧾" label="Чеків" value={summary?.sales_count||0} color="#6cb6ff" />
              <StatCard icon="📊" label="Середній чек" value={`${parseFloat(summary?.avg_check||0).toFixed(2)} ₴`} color="#5fbf8f" />
              <StatCard icon="🎁" label="Бонуси нараховано" value={`${parseFloat(summary?.bonus_earned||0).toFixed(2)}`} color="#f5a623" sub={`списано: ${parseFloat(summary?.bonus_used||0).toFixed(2)}`} />
            </div>

            {/* Графік по днях */}
            <div style={styles.chartCard}>
              <h3 style={styles.chartTitle}>📈 Виручка по днях</h3>
              {byDay.length === 0 || byDay.every(d => d.revenue === 0) ? (
                <div style={styles.noData}>Немає продажів за цей період</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={byDay} margin={{top:5, right:10, left:0, bottom:5}}>
                    <XAxis dataKey="label" tick={{fill:'#9a8a6e', fontSize:11}}
                      interval={Math.floor(byDay.length/7)} />
                    <YAxis tick={{fill:'#9a8a6e', fontSize:11}} />
                    <Tooltip
                      contentStyle={{background:'#211c14', border:'1px solid #38301f', borderRadius:'8px', color:'#fff'}}
                      formatter={(v) => [`${v} ₴`, 'Виручка']}
                      labelFormatter={(l) => `Дата: ${l}`}
                    />
                    <Bar dataKey="revenue" fill={CHART_COLOR} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={styles.twoCol}>
              {/* Графік по годинах */}
              <div style={styles.chartCard}>
                <h3 style={styles.chartTitle}>🕐 Активність по годинах (7 днів)</h3>
                {byHour.every(h => h.count === 0) ? (
                  <div style={styles.noData}>Немає даних</div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={byHour} margin={{top:5, right:10, left:0, bottom:5}}>
                      <XAxis dataKey="label" tick={{fill:'#9a8a6e', fontSize:10}}
                        interval={2} />
                      <YAxis tick={{fill:'#9a8a6e', fontSize:10}} />
                      <Tooltip
                        contentStyle={{background:'#211c14', border:'1px solid #38301f', borderRadius:'8px', color:'#fff'}}
                        formatter={(v) => [v, 'Чеків']}
                      />
                      <Line type="monotone" dataKey="count" stroke={CHART_COLOR2} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Розбивка по оплаті */}
              <div style={styles.chartCard}>
                <h3 style={styles.chartTitle}>💳 Способи оплати</h3>
                {paymentData.length === 0 ? (
                  <div style={styles.noData}>Немає даних</div>
                ) : (
                  <div style={styles.paymentWrap}>
                    <ResponsiveContainer width="50%" height={160}>
                      <PieChart>
                        <Pie data={paymentData} cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                          dataKey="value" paddingAngle={3}>
                          {paymentData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{background:'#211c14', border:'1px solid #38301f', borderRadius:'8px', color:'#fff'}}
                          formatter={(v) => [`${parseFloat(v).toFixed(2)} ₴`]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={styles.paymentLegend}>
                      {paymentData.map((p, i) => (
                        <div key={i} style={styles.paymentRow}>
                          <span style={{...styles.paymentDot, background: p.color}}/>
                          <span style={styles.paymentName}>{p.name}</span>
                          <span style={styles.paymentVal}>{parseFloat(p.value).toFixed(2)} ₴</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.twoCol}>
              {/* Топ товарів */}
              <div style={styles.chartCard}>
                <h3 style={styles.chartTitle}>🏆 Топ товарів по виручці</h3>
                {topProducts.length === 0 ? (
                  <div style={styles.noData}>Немає продажів</div>
                ) : (
                  <div style={styles.topList}>
                    {topProducts.map((p, i) => (
                      <div key={i} style={styles.topRow}>
                        <span style={styles.topNum}>{i+1}</span>
                        <span style={styles.topName}>{p.name}</span>
                        <span style={styles.topQty}>{parseFloat(p.quantity).toFixed(1)} {p.unit}</span>
                        <span style={styles.topRev}>{parseFloat(p.revenue).toFixed(2)} ₴</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Порівняння магазинів (тільки для мережі) */}
              {!selectedStoreId && (
                <div style={styles.chartCard}>
                  <h3 style={styles.chartTitle}>🏪 Магазини по виручці</h3>
                  {byStore.length === 0 ? (
                    <div style={styles.noData}>Немає продажів</div>
                  ) : (
                    <div style={styles.storeList}>
                      {byStore.map((s, i) => {
                        const maxRev = Math.max(...byStore.map(x => parseFloat(x.revenue)));
                        const pct = maxRev > 0 ? (parseFloat(s.revenue) / maxRev * 100) : 0;
                        return (
                          <div key={i} style={styles.storeRow}>
                            <div style={styles.storeRowTop}>
                              <span style={styles.storeRowName}>{s.store_name}</span>
                              <span style={styles.storeRowRev}>{parseFloat(s.revenue).toFixed(2)} ₴</span>
                            </div>
                            <div style={styles.storeBar}>
                              <div style={{...styles.storeBarFill, width:`${pct}%`}}/>
                            </div>
                            <div style={styles.storeRowMeta}>
                              {s.count} чеків · середній {parseFloat(s.avg_check).toFixed(2)} ₴
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Графік кількості чеків по днях */}
              {selectedStoreId && (
                <div style={styles.chartCard}>
                  <h3 style={styles.chartTitle}>🧾 Кількість чеків по днях</h3>
                  {byDay.every(d => d.count === 0) ? (
                    <div style={styles.noData}>Немає даних</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={byDay} margin={{top:5, right:10, left:0, bottom:5}}>
                        <XAxis dataKey="label" tick={{fill:'#9a8a6e', fontSize:10}}
                          interval={Math.floor(byDay.length/7)} />
                        <YAxis tick={{fill:'#9a8a6e', fontSize:10}} />
                        <Tooltip
                          contentStyle={{background:'#211c14', border:'1px solid #38301f', borderRadius:'8px', color:'#fff'}}
                          formatter={(v) => [v, 'Чеків']}
                        />
                        <Bar dataKey="count" fill={CHART_COLOR2} radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ ЗАЛИШКИ ══ */}
        {activeTab === 'stocks' && (
          <div>
            {!selectedStoreId ? (
              // Мережа — список магазинів з індикаторами
              <div>
                <h3 style={styles.sectionTitle}>Стан залишків по магазинах</h3>
                <div style={styles.networkGrid}>
                  {(stocks?.network || []).map(store => (
                    <div key={store.store_id} style={styles.networkStoreCard}
                      onClick={() => setSelectedStoreId(store.store_id)}>
                      <div style={styles.networkStoreName}>🏪 {store.store_name}</div>
                      <div style={styles.networkStoreMeta}>{store.store_address}</div>
                      <div style={styles.networkStoreBadges}>
                        <span style={styles.badgeTotal}>{store.total_products} поз.</span>
                        {store.low_stock_count > 0 && <span style={styles.badgeLow}>⚠️ {store.low_stock_count} мало</span>}
                        {store.out_of_stock_count > 0 && <span style={styles.badgeOut}>🔴 {store.out_of_stock_count} немає</span>}
                      </div>
                      <div style={styles.networkStoreArrow}>Деталі →</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Конкретний магазин
              <div>
                {stocks && (
                  <div style={styles.statsRow}>
                    <StatCard icon="📦" label="Позицій" value={stocks.summary?.total||0} />
                    <StatCard icon="⚠️" label="Закінчується" value={stocks.summary?.low_stock||0} color={stocks.summary?.low_stock > 0 ? '#f5a623' : '#fff'} />
                    <StatCard icon="🔴" label="Немає" value={stocks.summary?.out_of_stock||0} color={stocks.summary?.out_of_stock > 0 ? '#ff6b6b' : '#fff'} />
                    <StatCard icon="💰" label="Сума по собівартості" value={`${parseFloat(stocks.summary?.total_stock_value||0).toFixed(0)} ₴`} color="#5fbf8f" />
                  </div>
                )}

                <div style={styles.filterRow}>
                  <div style={styles.filterBtns}>
                    {[{key:'all',label:'Всі'},{key:'low',label:'⚠️ Мало'},{key:'out',label:'🔴 Немає'}].map(f => (
                      <button key={f.key}
                        style={stockFilter===f.key ? styles.filterBtnActive : styles.filterBtn}
                        onClick={() => changeStockFilter(f.key)}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <input style={styles.searchInput} placeholder="🔍 Пошук..."
                    value={stockSearch} onChange={e => setStockSearch(e.target.value)} />
                </div>

                {filteredStockProducts.length === 0 ? (
                  <p style={styles.noData}>
                    {stockFilter==='low' ? '✅ Немає товарів з низьким залишком' :
                     stockFilter==='out' ? '✅ Всі товари є в наявності' : 'Товарів не знайдено'}
                  </p>
                ) : (
                  <div className="table-wrap" style={{marginBottom:16}}>
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Товар</th>
                          <th style={{textAlign:'right'}}>Залишок</th>
                          <th style={{textAlign:'right'}}>Поріг</th>
                          <th style={{textAlign:'right'}}>Ціна</th>
                          <th style={{textAlign:'right'}}>Сума</th>
                          <th style={{textAlign:'center'}}>Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStockProducts.map(p => (
                          <tr key={p.id} style={p.is_out?styles.rowOut:p.is_low?styles.rowLow:undefined}>
                            <td>{p.name}{p.is_draft&&<span style={styles.tag}>🍺</span>}</td>
                            <td style={{textAlign:'right', fontWeight:700, whiteSpace:'nowrap',
                              color: p.is_out?'var(--red)':p.is_low?'var(--amber)':'var(--green)'}}>
                              {parseFloat(p.stock_quantity).toFixed(2)} {UNITS[p.unit]}
                            </td>
                            <td style={{textAlign:'right', fontSize:'12px', whiteSpace:'nowrap'}} className="muted">
                              {p.low_stock_threshold ? `${parseFloat(p.low_stock_threshold).toFixed(2)} ${UNITS[p.unit]}` : '—'}
                            </td>
                            <td style={{textAlign:'right', color:'var(--amber)', whiteSpace:'nowrap'}}>
                              {parseFloat(p.price).toFixed(2)} ₴
                            </td>
                            <td style={{textAlign:'right', color:'var(--green)', fontSize:'12px', whiteSpace:'nowrap'}}>
                              {parseFloat(p.stock_value).toFixed(2)} ₴
                            </td>
                            <td style={{textAlign:'center'}}>
                              {p.is_out ? <span style={styles.statusOut}>Немає</span>
                                : p.is_low ? <span style={styles.statusLow}>Мало</span>
                                : <span style={styles.statusOk}>✓</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Рух товарів */}
                <div style={styles.chartCard}>
                  <div style={styles.movHeader}>
                    <h3 style={styles.chartTitle}>📦 Топ руху товарів</h3>
                    <div style={styles.filterBtns}>
                      {[7,14,30].map(d => (
                        <button key={d}
                          style={movDays===d ? styles.filterBtnActive : styles.filterBtn}
                          onClick={() => { setMovDays(d); loadMovements(selectedStoreId, d); }}>
                          {d}д
                        </button>
                      ))}
                    </div>
                  </div>
                  {movements.length === 0 ? (
                    <div style={styles.noData}>Рухів не було</div>
                  ) : (
                    <div style={styles.topList}>
                      {movements.map((m, i) => (
                        <div key={i} style={styles.topRow}>
                          <span style={styles.topName}>{m.name}</span>
                          <span style={{color:'#5fbf8f', fontSize:'13px'}}>
                            {parseFloat(m.total_income)>0 ? `+${m.total_income} ${m.unit}` : '—'}
                          </span>
                          <span style={{color:'#ff6b6b', fontSize:'13px'}}>
                            {parseFloat(m.total_writeoff)>0 ? `-${m.total_writeoff} ${m.unit}` : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

const styles = {
  storeSelect: { padding:'8px 12px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'13px', cursor:'pointer' },
  daysBtns: { display:'flex', gap:'4px' },
  dayBtn: { padding:'7px 12px', background:'#38301f', color:'#9a8a6e', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px' },
  dayBtnActive: { padding:'7px 12px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px', fontWeight:'bold' },
  statsRow: { display:'flex', gap:'12px', marginBottom:'20px', flexWrap:'wrap' },
  sectionTitle: { color:'#f5a623', marginBottom:'16px' },
  chartCard: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'20px', marginBottom:'16px' },
  chartTitle: { color:'#f5a623', margin:'0 0 16px', fontSize:'15px' },
  twoCol: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', gap:'16px', marginBottom:'0' },
  noData: { color:'#9a8a6e', textAlign:'center', padding:'30px', fontSize:'14px' },
  paymentWrap: { display:'flex', alignItems:'center' },
  paymentLegend: { flex:1, padding:'0 16px' },
  paymentRow: { display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' },
  paymentDot: { width:'10px', height:'10px', borderRadius:'50%', flexShrink:0 },
  paymentName: { flex:1, fontSize:'13px' },
  paymentVal: { fontWeight:'bold', fontSize:'13px', color:'#f5a623' },
  topList: { display:'flex', flexDirection:'column', gap:'6px' },
  topRow: { display:'flex', alignItems:'center', gap:'12px', padding:'8px 12px', background:'#1c1812', borderRadius:'8px' },
  topNum: { color:'#f5a623', fontWeight:'bold', fontSize:'15px', minWidth:'20px' },
  topName: { flex:1, fontSize:'13px' },
  topQty: { color:'#9a8a6e', fontSize:'12px' },
  topRev: { fontWeight:'bold', color:'#f5a623', fontSize:'13px' },
  storeList: { display:'flex', flexDirection:'column', gap:'12px' },
  storeRow: {},
  storeRowTop: { display:'flex', justifyContent:'space-between', marginBottom:'4px' },
  storeRowName: { fontWeight:'bold', fontSize:'14px' },
  storeRowRev: { color:'#f5a623', fontWeight:'bold' },
  storeBar: { height:'6px', background:'#38301f', borderRadius:'3px', marginBottom:'4px' },
  storeBarFill: { height:'6px', background:'#f5a623', borderRadius:'3px' },
  storeRowMeta: { color:'#9a8a6e', fontSize:'12px' },
  networkGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:'16px' },
  networkStoreCard: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'20px', cursor:'pointer' },
  networkStoreName: { fontWeight:'bold', fontSize:'15px', marginBottom:'4px' },
  networkStoreMeta: { color:'#9a8a6e', fontSize:'12px', marginBottom:'10px' },
  networkStoreBadges: { display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'10px' },
  networkStoreArrow: { color:'#f5a623', fontSize:'13px' },
  badgeTotal: { fontSize:'11px', background:'#38301f', color:'#9a8a6e', padding:'2px 8px', borderRadius:'10px' },
  badgeLow: { fontSize:'11px', background:'#3d2800', color:'#f5a623', padding:'2px 8px', borderRadius:'10px' },
  badgeOut: { fontSize:'11px', background:'#3d0000', color:'#ff6b6b', padding:'2px 8px', borderRadius:'10px' },
  filterRow: { display:'flex', gap:'12px', marginBottom:'14px', alignItems:'center', flexWrap:'wrap' },
  filterBtns: { display:'flex', gap:'6px' },
  filterBtn: { padding:'6px 14px', background:'#38301f', color:'#9a8a6e', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px' },
  filterBtnActive: { padding:'6px 14px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px', fontWeight:'bold' },
  searchInput: { flex:1, minWidth:'150px', padding:'8px 12px', borderRadius:'8px', border:'1px solid #38301f', background:'#211c14', color:'#fff', fontSize:'13px' },
  rowLow: { background:'#1a1200' },
  rowOut: { background:'#1a0000' },
  tag: { marginLeft:'5px', fontSize:'11px' },
  statusOk: { color:'#5fbf8f', fontSize:'12px' },
  statusLow: { background:'#3d2800', color:'#f5a623', padding:'2px 6px', borderRadius:'8px', fontSize:'11px' },
  statusOut: { background:'#3d0000', color:'#ff6b6b', padding:'2px 6px', borderRadius:'8px', fontSize:'11px' },
  movHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' },
};
